import { z } from 'zod';
import { ObjectIdSchema } from '../../shared/object-id.schema';
/**
 * Why a draft assignment produced no live invite on publish. `no-inputs`
 * rejects assignments that never acquired an upstream edge; `clean` covers
 * both pristine re-imports (dirtyForInvite=false, not force-invited) and
 * assignments whose target invite already exists in the live collection.
 */ export const InviteSkipReasonSchema = z.enum([
    'no-inputs',
    'clean'
]);
/**
 * POST /api/v2/process/:id/draft/publish — request body. `force` skips the
 * `draft-stale` content-staleness check (live steps modified after draft
 * creation). It does NOT skip `draft-modified-during-publish` — that's a real
 * write-write race the OCC gate has to catch regardless of user intent.
 */ export const PublishDraftRequestSchema = z.object({
    force: z.boolean().optional()
});
/**
 * POST /api/v2/process/:id/draft/publish — success response
 */ export const PublishDraftResponseSchema = z.object({
    publishedAt: z.date(),
    stepsCreated: z.number(),
    stepsUpdated: z.number(),
    stepsDeleted: z.number(),
    invitesSent: z.array(z.object({
        stepId: ObjectIdSchema,
        recipientId: ObjectIdSchema
    })),
    invitesSkipped: z.array(z.object({
        stepId: ObjectIdSchema,
        reason: InviteSkipReasonSchema
    })),
    invitesFailed: z.array(z.object({
        stepId: ObjectIdSchema,
        recipientId: ObjectIdSchema,
        error: z.string()
    }))
});
/**
 * Publish validation error body (HTTP 400)
 */ export const PublishValidationErrorSchema = z.object({
    contentErrors: z.array(z.object({
        stepId: ObjectIdSchema,
        issues: z.array(z.object({
            path: z.array(z.union([
                z.string(),
                z.number()
            ])),
            message: z.string(),
            code: z.string()
        }))
    })),
    graphErrors: z.array(z.object({
        // Optional: `no-start-event` is a process-level error (the draft has
        // no start event node), so there's no per-step id to attach.
        stepId: ObjectIdSchema.optional(),
        reason: z.enum([
            'no-inputs',
            'no-outputs',
            'orphan',
            'no-start-event',
            'event-disconnected',
            'rule-no-output',
            'unreachable'
        ]),
        // Set only for `rule-no-output` — identifies which decision rule on
        // the step is stranded (unconnected and not marked terminal).
        ruleId: ObjectIdSchema.optional()
    }))
});
/**
 * Why a publish attempt conflicted. `draft-stale` means the draft references
 * live steps that changed under us (content-level staleness). `draft-modified-
 * during-publish` means the OCC delete lost a race with an in-flight PATCH
 * (the draft's `updatedAt` moved between read and delete).
 */ export const PublishConflictReason = {
    DRAFT_STALE: 'draft-stale',
    DRAFT_MODIFIED_DURING_PUBLISH: 'draft-modified-during-publish'
};
/**
 * One item in a list-style diff. `id` is a stable key used by the FE only for
 * React keying; `label` is the already-resolved display string (ref names are
 * resolved server-side so the client doesn't need to know each field's shape).
 */ export const StepFieldListItemSchema = z.object({
    id: z.string(),
    label: z.string()
});
/**
 * Per-field diff entry inside a `draft-stale` 409 body.
 *
 *  - `value`: simple scalar fields (taskDescription, output, generatedName,
 *    generatedDescription, actionType, assignedTo). Carries concrete
 *    before/after values for side-by-side rendering.
 *
 *  - `list`: array/object fields (rules, inputs, helpers.*, whoElseDoesThis).
 *    Items present on only one side go to `added` (in draft, not in live) or
 *    `removed` (in live, not in draft). `modified` is optional — only rules
 *    track in-place edits (same `_id`, different content). All labels are
 *    server-resolved.
 */ export const StepFieldChangeSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('value'),
        field: z.string(),
        live: z.unknown(),
        draft: z.unknown()
    }),
    z.object({
        kind: z.literal('list'),
        field: z.string(),
        added: z.array(StepFieldListItemSchema),
        removed: z.array(StepFieldListItemSchema),
        modified: z.array(z.object({
            id: z.string(),
            liveLabel: z.string(),
            draftLabel: z.string()
        })).optional()
    })
]);
export const StepConflictSchema = z.object({
    stepId: ObjectIdSchema,
    stepLabel: z.string(),
    changes: z.array(StepFieldChangeSchema)
});
/**
 * Publish conflict error body (HTTP 409). `conflicts` is populated only for
 * `draft-stale`; the `draft-modified-during-publish` race has no diff data
 * (the OCC check fires at the transactional delete, after the user-facing
 * detection point).
 */ export const PublishConflictErrorSchema = z.object({
    reason: z.enum([
        PublishConflictReason.DRAFT_STALE,
        PublishConflictReason.DRAFT_MODIFIED_DURING_PUBLISH
    ]),
    conflictingStepIds: z.array(ObjectIdSchema).optional(),
    conflicts: z.array(StepConflictSchema).optional()
});

//# sourceMappingURL=publish-process-draft.schema.js.map